/**
 * Boutique de gemmes et de packs (§14, V2 Stripe).
 *
 * **Ce qui est vendu, et ce qui ne le sera jamais.** Le §14 est catégorique : *pas de
 * pay-to-win sur la production*. Aucun pack ne vend donc de pièces, de multiplicateur,
 * ni d'accélération de production permanente. On vend des **gemmes**, qui achètent des
 * cosmétiques (skins, thèmes d'île) et des accélérations ponctuelles — jamais un
 * avantage que le temps de jeu ne rattrape pas.
 *
 * **L'écran existe avant le paiement, et le dit.** Stripe arrive en V2 ; les packs sont
 * donc affichés avec leurs prix réels et un bouton qui annonce honnêtement la
 * disponibilité. Un écran de boutique absent laisse croire que le jeu n'a pas de modèle
 * économique ; un écran qui simule un achat serait un mensonge. Celui-ci montre l'offre
 * et dit où elle en est.
 *
 * Les 174 combinaisons de skins (29 bâtiments × 6 palettes du pack Synty) sont ce que
 * ces gemmes achèteront : de la variété cosmétique, sans un seul asset supplémentaire.
 */

import { ECONOMY, GEM_SPENDING, REWARDED_AD } from '@/config/balance';
import { dayNumber } from '@/core/quests';
import { duration, n, t } from '@/i18n';

import { skipsUsedToday } from '@/core/gameState';
import type { CitySnapshot } from '@/core/gameState';
import { createButton, createPanel } from './components';
import { createTabbedSection } from './tabbedSection';
import { createModal } from './components/overlays';
import type { ModalHandle } from './components/overlays';
import { icon } from './icons';

interface Pack {
  readonly id: string;
  readonly gems: number;
  /** Prix en centimes d'euro : jamais un flottant, l'arrondi monétaire ne pardonne pas. */
  readonly cents: number;
  /** Gemmes offertes en plus, mises en avant sur les gros packs. */
  readonly bonus: number;
}

/**
 * Progression volontairement lisible : le prix par gemme baisse avec la taille du pack,
 * ce que le bonus affiché rend explicite. Un joueur doit pouvoir comparer sans calculer.
 */
const PACKS: readonly Pack[] = [
  { id: 'handful', gems: 80, cents: 199, bonus: 0 },
  { id: 'pouch', gems: 250, cents: 499, bonus: 20 },
  { id: 'chest', gems: 650, cents: 1099, bonus: 90 },
  { id: 'vault', gems: 1500, cents: 1999, bonus: 300 },
];

function formatPrice(cents: number, locale: string): string {
  const value = (cents / 100).toFixed(2);
  return locale === 'fr' ? `${value.replace('.', ',')} €` : `€${value}`;
}

export interface StorePanelHandle {
  /**
   * Ouvre la boutique.
   *
   * `tab` choisit l'onglet : `'buy'` quand le joueur vient chercher des gemmes (un
   * bouton « obtenir des gemmes » qui ouvre l'onglet des dépenses lui ferait faire un
   * tap de plus pour la chose qu'il avait déjà demandée), `'spend'` par défaut.
   */
  open(tab?: 'spend' | 'buy'): void;
  update(snapshot: CitySnapshot): void;
  destroy(): void;
}

/** Ce que le joueur peut FAIRE de ses gemmes. Sans ces rappels, elles ne servent à rien. */
export interface StorePanelCallbacks {
  readonly onRush: (tierId: string) => void;
  readonly onLand: () => void;
  /** Regarder une pub récompensée (×2 pendant 1 h, plafonné par jour). */
  readonly onWatchAd?: () => void;
  /** Avance de production : le palier est NOMMÉ, jamais un index (règle 22). */
  readonly onSkip?: (tierId: string) => void;
  /** Nuit blanche : desserre le plafond hors-ligne du prochain retour. */
  readonly onNight?: () => void;
  /** Acheter un pack de gemmes — la chaîne Stripe (0.38.0). */
  readonly onBuy?: (packId: string) => void;
}

/**
 * Nomme un contrôle — et le fait DANS son expression d'initialisation.
 *
 * ══ ⚠️ CONTOURNEMENT D'UN DÉFAUT DU MINIFICATEUR ════════════════════════════
 *
 * Chaque ligne de dépense doit porter une classe qui la NOMME : les sondes ne
 * doivent jamais viser « le premier bouton de la liste » (règle 22). La forme
 * évidente est en deux instructions :
 *
 *     const landButton = createButton({ … });
 *     landButton.el.classList.add('store-spend__land');
 *
 * Elle est juste, et le bundle de production la casse. Mesuré le 18 août dans
 * `dist/assets/main-*.js` : le minificateur a REMONTÉ le `classList.add` au-dessus
 * du `let` qui déclare la variable, produisant un accès avant initialisation —
 *
 *     p.el.classList.add(`store-spend__rush`), h.el.classList.add(`store-spend__land`);
 *     … let h = H({ … });
 *
 * Conséquence : **le jeu ne démarrait plus du tout**, écran de démarrage figé sur
 * « ReferenceError: Cannot access 'h' before initialization ». Le serveur de
 * développement ne bundle pas et fonctionnait parfaitement ; `typecheck`, `lint`
 * et les 473 tests étaient verts. Seule une sonde qui charge le BUILD l'a vu.
 *
 * En passant par cette fonction, le rattachement fait partie de l'initialisation :
 * il n'existe plus d'instruction séparée à déplacer, et le défaut devient
 * irreprésentable — le principe suivi partout ici, rendre l'erreur impossible par
 * construction plutôt que la corriger là où elle se voit.
 */
function named<T extends { readonly el: HTMLElement }>(handle: T, className: string): T {
  /*
    Découpé sur les espaces : `classList.add` REFUSE un jeton qui en contient
    (`InvalidCharacterError`), et l'appelant a de bonnes raisons d'en passer deux —
    une classe de famille et une classe de variante. Laisser l'appelant y penser,
    c'est garantir qu'un jour il oubliera, et l'erreur tombe au démarrage : elle a
    empêché le jeu entier de s'ouvrir le temps d'un build.
  */
  handle.el.classList.add(...className.split(/\s+/).filter((token) => token !== ''));
  return handle;
}

/** Libellé d'une ligne de dépense — une seule forme, pour que les deux se ressemblent. */
function labelled(text: string): HTMLElement {
  const el = document.createElement('span');
  el.className = 'store-spend__label';
  el.textContent = text;
  return el;
}

export function createStorePanel(
  locale: () => string,
  callbacks?: StorePanelCallbacks,
): StorePanelHandle {
  /*
    Ni blason ni texte d'intro : le HÉROS au cristal porte déjà l'identité et le
    message (« ton trésor », le solde en grand, l'indice). Le blason gemme au-dessus
    doublonnait le cristal, l'intro doublonnait l'indice — et ces ~120 px étaient
    exactement ce qui empêchait la boutique de tenir dans un écran de portable.
  */
  const modal: ModalHandle = createModal({
    title: t('store.title'),
  });

  /*
    Le modal doit être MONTÉ : `createModal` fabrique l'élément, il ne le pose pas.
    Sans cette ligne il n'existe nulle part dans le document, et `open()` bascule une
    classe sur un nœud que personne n'affiche — le clic semblait sans effet.

    Monté sur `body` et non dans le HUD : une boîte de dialogue doit couvrir toute la
    page, et un parent en `pointer-events: none` la rendrait inerte.
  */
  document.body.append(modal.el);
  // La boutique a plus à montrer qu'une confirmation : sa modale s'élargit sur
  // ordinateur (capture de Benoit — les packs s'écrasaient dans une colonne étroite).
  modal.el.classList.add('store-scrim');

  /*
    ── Le trésor, en héros ────────────────────────────────────────────────────
    Maquette « Boutique de gemmes Villopia » (Claude Design, 18 août) : un grand
    cristal low-poly avec son halo, le solde en très grand — une boutique de jeu
    qui donne envie, pas un relevé. Le cristal est dessiné ici, dans le registre
    du jeu d'icônes : facettes plates, éclats blancs.
  */
  const hero = document.createElement('div');
  hero.className = 'store-hero';
  hero.innerHTML = `
    <span class="store-hero__crystal" aria-hidden="true">
      <svg viewBox="0 0 96 96">
        <polygon points="48,6 78,30 66,84 30,84 18,30" fill="#3fd3cb"/>
        <polygon points="48,6 78,30 48,42" fill="#8ff0f5"/>
        <polygon points="48,6 18,30 48,42" fill="#5ee0da"/>
        <polygon points="18,30 30,84 48,42" fill="#1a8d88"/>
        <polygon points="78,30 66,84 48,42" fill="#2fb5ae"/>
        <polygon points="30,84 66,84 48,42" fill="#12706c"/>
        <circle cx="70" cy="16" r="3" fill="#fdf7ea" opacity="0.9"/>
        <circle cx="24" cy="52" r="2" fill="#fdf7ea" opacity="0.7"/>
      </svg>
    </span>`;

  const heroText = document.createElement('div');
  heroText.className = 'store-hero__text';
  const heroLabel = document.createElement('span');
  heroLabel.className = 'store-hero__label';
  heroLabel.textContent = t('store.treasure');
  const balance = document.createElement('b');
  balance.className = 'store-hero__balance';
  const heroHint = document.createElement('span');
  heroHint.className = 'store-hero__hint';
  heroHint.textContent = t('store.treasureHint');
  heroText.append(heroLabel, balance, heroHint);
  hero.append(heroText);

  /*
    ── DÉPENSER passe AVANT acheter ──────────────────────────────────────────

    Jusqu'à l'audit du 17 août, les gemmes n'avaient **aucune dépense dans tout le
    dépôt** : trois quêtes par jour, trois par semaine, une chaîne de connexion et
    soixante-dix jalons récompensaient une monnaie morte. Cet écran vendait des gemmes
    sans que rien n'en consomme — il proposait donc au joueur d'acheter un compteur.

    La section des dépenses est en tête, et les packs derrière : on montre d'abord à quoi
    ça sert, ensuite comment en avoir plus. L'ordre inverse serait celui d'une boutique
    qui a quelque chose à vendre, pas d'un jeu qui a quelque chose à offrir.
  */
  const spendTitle = document.createElement('h4');
  spendTitle.className = 'store-spend__title';
  spendTitle.textContent = t('gems.title');

  const spendList = document.createElement('div');
  spendList.className = 'store-spend';

  /*
    ── TROIS PALIERS SUR UNE SEULE RANGÉE (G§3, A2) ──────────────────────────

    Une offre unique à 20 gemmes plafonnait la dépense d'un joueur régulier bien en
    dessous de son revenu (≈ 29 gemmes/jour, mesuré au G§1). Trois paliers donnent
    au joueur du soir une offre à sa taille et au gros solde une raison d'exister.

    Côte à côte plutôt qu'en trois rangées : la boutique doit tenir dans un écran de
    téléphone — c'est ce que la refonte venait de régler, et trois lignes de plus
    l'auraient défait. Segmentés, les paliers se COMPARENT d'un coup d'œil, ce qui
    est précisément la décision qu'on demande au joueur.
  */
  const rushGroup = document.createElement('div');
  rushGroup.className = 'store-spend__tiers';
  rushGroup.setAttribute('role', 'group');
  rushGroup.setAttribute('aria-label', t('gems.rushTitle'));

  const rushButtons = GEM_SPENDING.rush.map((tier) =>
    named(
      createButton({
        // Le multiplicateur ET la durée, sur deux lignes : « ×3 » seul ne dit pas
        // ce qu'on achète, et le prix seul ne dit pas ce qu'on obtient.
        label: t('gems.rushTier', {
          value: n(tier.multiplier),
          duration: duration(tier.durationMs),
        }),
        variant: tier.id === 'gust' ? 'primary' : 'secondary',
        onClick: () => {
          callbacks?.onRush(tier.id);
        },
      }),
      `store-spend__rush store-spend__rush--${tier.id}`,
    ),
  );
  for (const button of rushButtons) rushGroup.append(button.el);

  const rushRow = createPanel({
    children: [labelled(t('gems.rushTitle')), rushGroup],
  });
  rushRow.el.classList.add('store-spend__row', 'store-spend__row--tiers');

  /*
    ── L'AVANCE DE PRODUCTION, ET POURQUOI ELLE EST LA MOINS CHÈRE ───────────

    Défaut n° 2 du G§1 : l'offre la moins chère coûtait 20 gemmes, alors que les
    premières gemmes tombent à l'unité (`firstOfType: 1`). La décision d'achat
    n'existait donc pas le premier jour — et un joueur qui n'a jamais dépensé
    n'achètera jamais. Le premier palier est à 15, juste après l'offre gratuite.

    Le libellé EST une durée, pas un multiplicateur : c'est la Loi 2 du G§0 — un
    prix se lit en temps de jeu. « 4 h » dit exactement ce qu'on achète, là où
    « +12 000 pièces » ne veut rien dire dans un jeu où la production décuple.
  */
  const skipGroup = document.createElement('div');
  skipGroup.className = 'store-spend__tiers';
  skipGroup.setAttribute('role', 'group');
  skipGroup.setAttribute('aria-label', t('gems.skipTitle'));

  const skipButtons = GEM_SPENDING.skip.tiers.map((tier) =>
    named(
      createButton({
        label: t('gems.skipTierCost', {
          duration: duration(tier.seconds * 1000),
          cost: n(tier.cost),
        }),
        variant: tier.id === 'evening' ? 'primary' : 'secondary',
        onClick: () => {
          callbacks?.onSkip?.(tier.id);
        },
      }),
      `store-spend__skip store-spend__skip--${tier.id}`,
    ),
  );
  for (const button of skipButtons) skipGroup.append(button.el);

  // Le libellé porte le QUOTA du jour : « il m'en reste combien » est l'information
  // de la décision, comme pour la pub. Il change donc à chaque `update`.
  const skipLabel = labelled(t('gems.skipTitle'));
  const skipRow = createPanel({ children: [skipLabel, skipGroup] });
  skipRow.el.classList.add('store-spend__row', 'store-spend__row--tiers');

  const nightButton = named(
    createButton({
      label: t('gems.nightCost', { value: n(GEM_SPENDING.night.cost) }),
      variant: 'secondary',
      icon: 'clock',
      onClick: () => {
        callbacks?.onNight?.();
      },
    }),
    'store-spend__night',
  );

  const nightRow = createPanel({ children: [labelled(t('gems.night')), nightButton.el] });
  nightRow.el.classList.add('store-spend__row');

  const landButton = named(
    createButton({
      label: t('gems.landCost', { value: n(GEM_SPENDING.land.cost) }),
      variant: 'secondary',
      icon: 'city',
      onClick: () => {
        callbacks?.onLand();
      },
    }),
    'store-spend__land',
  );

  const landRow = createPanel({ children: [labelled(t('gems.land')), landButton.el] });
  landRow.el.classList.add('store-spend__row');

  /*
    La pub récompensée EN TÊTE des dépenses : c'est la seule offre gratuite, et elle
    récompense le TEMPS — exactement le contrat du §14. Le compteur du jour est dans
    le bouton : « il m'en reste combien » est l'information de la décision.
  */
  const adButton = named(
    createButton({
      label: '',
      variant: 'primary',
      icon: 'eye',
      onClick: () => {
        callbacks?.onWatchAd?.();
      },
    }),
    'store-spend__ad',
  );

  const adRow = createPanel({
    children: [
      labelled(
        t('ads.offer', {
          value: n(REWARDED_AD.multiplier),
          duration: duration(REWARDED_AD.durationMs),
        }),
      ),
      adButton.el,
    ],
  });
  adRow.el.classList.add('store-spend__row', 'store-spend__row--ad');

  /*
    ── L'ORDRE EST UNE ÉCHELLE DE PRIX, PAS UN CLASSEMENT D'IMPORTANCE ────────

    Gratuit (pub), 15, 20, 25, 45. Un joueur qui descend la liste rencontre d'abord
    ce qu'il peut se payer : c'est ce qui rend la première dépense possible le
    premier jour (défaut n° 2 du G§1). L'ordre inverse — le plus cher d'abord —
    est celui d'une boutique qui vise le portefeuille plutôt que l'usage.
  */
  spendList.append(adRow.el, skipRow.el, rushRow.el, nightRow.el, landRow.el);

  const list = document.createElement('div');
  list.className = 'store-list';

  /*
    Un TAS de gemmes par pack, qui grossit d'un pack à l'autre (maquette validée par
    Benoit) : la valeur se VOIT avant de se lire. Low-poly plat, mêmes teintes que
    le cristal du héros.
  */
  const PILES: Record<string, string> = {
    handful: `<polygon points="40,12 54,24 48,44 32,44 26,24" fill="#3fd3cb"/>
      <polygon points="40,12 54,24 40,30" fill="#8ff0f5"/>
      <polygon points="32,44 48,44 40,30" fill="#12706c"/>`,
    pouch: `<polygon points="28,20 40,28 36,46 22,46 18,30" fill="#2fb5ae"/>
      <polygon points="52,14 66,26 60,46 44,46 40,26" fill="#3fd3cb"/>
      <polygon points="52,14 66,26 52,32" fill="#8ff0f5"/>
      <circle cx="68" cy="14" r="2.4" fill="#fdf7ea" opacity="0.85"/>`,
    chest: `<polygon points="22,26 34,32 30,50 16,50 12,34" fill="#2fb5ae"/>
      <polygon points="42,10 58,22 52,50 32,50 28,24" fill="#3fd3cb"/>
      <polygon points="42,10 58,22 42,30" fill="#8ff0f5"/>
      <polygon points="62,26 72,34 68,50 56,50 54,32" fill="#5ee0da"/>
      <circle cx="70" cy="12" r="2.6" fill="#fdf7ea" opacity="0.9"/>
      <circle cx="14" cy="22" r="2" fill="#fdf7ea" opacity="0.7"/>`,
    vault: `<polygon points="16,30 26,36 22,52 10,52 8,36" fill="#2fb5ae"/>
      <polygon points="34,14 48,24 44,52 26,52 22,26" fill="#3fd3cb"/>
      <polygon points="34,14 48,24 34,32" fill="#8ff0f5"/>
      <polygon points="56,8 70,20 66,52 48,52 46,20" fill="#5ee0da"/>
      <polygon points="56,8 70,20 56,26" fill="#bdfaf7"/>
      <circle cx="74" cy="10" r="2.8" fill="#fdf7ea" opacity="0.95"/>
      <circle cx="12" cy="24" r="2" fill="#fdf7ea" opacity="0.7"/>
      <circle cx="40" cy="6" r="1.8" fill="#fdf7ea" opacity="0.8"/>`,
  };

  for (const pack of PACKS) {
    const total = pack.gems + pack.bonus;

    const pile = document.createElement('span');
    pile.className = 'store-pack__pile';
    pile.setAttribute('aria-hidden', 'true');
    pile.innerHTML = `<svg viewBox="0 0 80 60">${PILES[pack.id] ?? ''}</svg>`;

    const amount = document.createElement('span');
    amount.className = 'store-pack__amount';
    amount.append(icon('gem'), n(total));

    const bonus = document.createElement('span');
    bonus.className = 'store-pack__bonus';
    // Pas de pastille « +0 » sur le petit pack : une mention vide fait du bruit et
    // dévalorise celles qui disent quelque chose.
    if (pack.bonus > 0) bonus.textContent = t('store.bonus', { value: n(pack.bonus) });

    const buy = createButton({
      label: formatPrice(pack.cents, locale()),
      variant: pack.id === 'chest' ? 'primary' : 'secondary',
      // La chaîne Stripe existe (0.38.0). Tant que la clé n'est pas posée côté
      // serveur, le clic répond honnêtement « bientôt » — jamais un bouton muet.
      onClick: () => {
        callbacks?.onBuy?.(pack.id);
      },
    });
    buy.el.classList.add('store-pack__buy');
    buy.el.dataset.pack = pack.id;

    const card = createPanel({ children: [pile, amount, bonus, buy.el] });
    card.el.classList.add('store-pack');
    if (pack.id === 'chest') {
      // Le pack POPULAIRE se détache : bordure or, halo, badge — la maquette de
      // Benoit, au pixel d'intention près.
      card.el.classList.add('store-pack--popular');
      const badge = document.createElement('span');
      badge.className = 'store-pack__badge';
      badge.textContent = t('store.popular');
      card.el.prepend(badge);
    }
    list.append(card.el);
  }

  const notice = document.createElement('p');
  notice.className = 'store-notice';
  notice.textContent = t('store.soon');

  /*
    ── OBLIGATION LÉGALE, pas une mention de bas de page ─────────────────────

    Les gemmes sont un contenu numérique livré immédiatement. L'article L221-28
    du code de la consommation ne fait perdre le droit de rétractation de
    quatorze jours QUE si l'acheteur a demandé cette livraison immédiate **et
    reconnu** cette perte, avant de payer. Dire ça après l'achat n'aurait aucune
    valeur.

    La phrase est posée SOUS la grille des packs, et c'est suffisant : elle est
    visible en même temps qu'eux (mesuré), et la commande ne se valide pas ici —
    elle se valide sur la page de Stripe, après. Le joueur l'a donc lue avant de
    confirmer quoi que ce soit. La placer au-dessus des packs repousserait l'offre
    hors de l'écran, ce que la refonte de la boutique venait précisément de régler.

    Le lien s'ouvre dans un nouvel onglet — personne ne doit perdre son panier
    pour aller lire les conditions.
  */
  const terms = document.createElement('p');
  terms.className = 'store-terms';
  terms.append(t('store.termsBefore'));
  const termsLink = document.createElement('a');
  termsLink.href = '/legal#cgv';
  termsLink.target = '_blank';
  termsLink.rel = 'noopener';
  termsLink.textContent = t('store.termsLink');
  terms.append(termsLink, t('store.termsAfter'));

  // La promesse du §14, écrite noir sur blanc dans l'écran qui vend : c'est là qu'elle
  // engage, pas dans un document que personne ne lit.
  const fairness = document.createElement('p');
  fairness.className = 'store-fairness';
  fairness.append(icon('shield'), t('store.fairness'));

  /*
    ══ LA BOUTIQUE EN DEUX ONGLETS (demande de Benoit, 20 août) ═══════════════

    Elle empilait tout dans une seule colonne : le solde, six offres de dépense, la
    grille des packs, et trois paragraphes légaux. Le joueur qui venait dépenser
    devait passer devant ce qui se vend, et celui qui venait acheter devait défiler
    six offres — chacun traversait ce qu'il ne cherchait pas.

    **Dépenser d'abord**, et ce n'est pas un hasard : `GEMMES.md` construit tout sur
    l'idée qu'on n'achète une monnaie que si on sait ce qu'elle fait. L'onglet qui
    montre à quoi servent les gemmes est le meilleur argument pour en acheter — le
    mettre en second reviendrait à vendre avant d'avoir expliqué.

    ⚠️ **Les mentions légales restent avec les packs**, dans le MÊME onglet. L'article
    L221-28 exige que le joueur ait reconnu la livraison immédiate **avant** de payer :
    la phrase doit être visible en même temps que l'offre. Les séparer par un onglet
    casserait précisément la propriété qui la rend valable.

    Le solde, lui, reste au-dessus des onglets : il concerne les deux.
  */
  const tabs = createTabbedSection([
    {
      id: 'spend',
      label: t('store.tabSpend'),
      icon: 'bolt',
      content: [spendTitle, spendList],
    },
    {
      id: 'buy',
      label: t('store.tabBuy'),
      icon: 'gem',
      content: [list, terms, notice, fairness],
    },
  ]);

  modal.content.append(hero, tabs.el);

  const close = createButton({
    label: t('common.close'),
    variant: 'ghost',
    onClick: () => {
      modal.close();
    },
  });
  modal.actions.append(close.el);

  return {
    open: (tab) => {
      if (tab !== undefined) tabs.select(tab);
      modal.open();
    },
    update(snapshot) {
      balance.textContent = t('store.balanceShort', { value: n(snapshot.gems) });

      /*
        Un bouton qu'on ne peut pas presser DOIT dire pourquoi.

        Trois raisons distinctes, trois libellés : pas assez de gemmes, accélération déjà
        en cours, terrain au maximum. Un bouton grisé muet laisse croire à une panne — et
        c'est exactement le défaut que l'audit a relevé sur les cartes de la boutique,
        où « trop cher » et « verrouillé » se ressemblaient.
      */
      /*
        Chaque palier dit son PRIX et son état. Un palier déjà couvert n'est plus
        refusé : racheter PROLONGE (voir `grantBoost`), et c'est le correctif du
        défaut n° 3 du G§1 — l'offre était grisée précisément quand le joueur
        voulait payer.
      */
      rushButtons.forEach((button, index) => {
        const tier = GEM_SPENDING.rush[index];
        if (tier === undefined) return;
        button.setDisabled(snapshot.gems < tier.cost);
        button.setLabel(
          t('gems.rushTierCost', {
            value: n(tier.multiplier),
            duration: duration(tier.durationMs),
            cost: n(tier.cost),
          }),
        );
      });

      /*
        L'avance de production porte DEUX refus distincts, et ils ne se disent pas
        pareil : « pas assez de gemmes » grise le palier seul, « quota du jour
        épuisé » grise la rangée entière et le dit dans le libellé. Un bouton grisé
        muet laisse croire à une panne — c'est le reproche de l'audit aux cartes de
        la boutique, où « trop cher » et « verrouillé » se ressemblaient.
      */
      const skipsLeft = Math.max(
        0,
        GEM_SPENDING.skip.dailyCap - skipsUsedToday(snapshot, Date.now()),
      );
      skipLabel.textContent =
        skipsLeft === 0 ? t('gems.skipExhausted') : t('gems.skipLeft', { value: n(skipsLeft) });
      skipButtons.forEach((button, index) => {
        const tier = GEM_SPENDING.skip.tiers[index];
        if (tier === undefined) return;
        button.setDisabled(skipsLeft === 0 || snapshot.gems < tier.cost);
      });

      // La nuit blanche est un objet en RÉSERVE, pas un effet en cours : le bouton
      // dit laquelle des deux situations est vraie, sans quoi le joueur rachèterait.
      nightButton.setDisabled(snapshot.nightPass || snapshot.gems < GEM_SPENDING.night.cost);
      nightButton.setLabel(
        snapshot.nightPass
          ? t('gems.nightReady')
          : t('gems.nightCost', { value: n(GEM_SPENDING.night.cost) }),
      );

      const maxed = snapshot.landPurchases >= ECONOMY.land.maxPurchases;
      landButton.setDisabled(maxed || snapshot.gems < GEM_SPENDING.land.cost);
      landButton.setLabel(
        maxed ? t('city.landMaxed') : t('gems.landCost', { value: n(GEM_SPENDING.land.cost) }),
      );

      // Pub récompensée : le quota du jour vit dans le bouton. Un jour neuf remet le
      // compteur en lecture — l'écriture se fait à la récompense, côté jeu.
      // Pendant une accélération, le bouton GARDE son identité (le quota) : deux
      // lignes affichant le même « Accélération en cours » se lisaient comme un
      // doublon (capture de Benoit) — c'est la ligne du rush qui porte cet état.
      const watchedToday = snapshot.adsDay === dayNumber(Date.now()) ? snapshot.adsWatched : 0;
      const adsLeft = Math.max(0, REWARDED_AD.dailyCap - watchedToday);
      /*
        ⚠️ Plus de `|| rushing`. La pub a son propre emplacement de boost depuis
        A3 : la griser pendant une accélération payante était le défaut n° 3 du
        G§1 — les deux sources se cumulent maintenant, il n'y a plus de raison de
        refuser la gratuite parce que la payante tourne.
      */
      adButton.setDisabled(adsLeft === 0);
      adButton.setLabel(adsLeft === 0 ? t('ads.exhausted') : t('ads.watch', { value: n(adsLeft) }));
    },
    destroy() {
      modal.destroy();
    },
  };
}
